package uk.co.nickthecoder.kojak

import kotlin.math.max
import kotlin.math.min

object Json {

    /**
     * Represents the JSON literal `null`.
     */
    val NULL = JsonLiteral("null")

    /**
     * Represents the JSON literal `true`.
     */
    val TRUE = JsonLiteral("true")

    /**
     * Represents the JSON literal `false`.
     */
    val FALSE = JsonLiteral("false")

    /**
     * Returns a JsonValue instance that represents the given <code>int</code> value.
     *
     * @param value
     *          the value to get a JSON representation for
     * @return a JSON value that represents the given value
     */
    fun value(value: Int): JsonValue = JsonNumber(value.toString()) //Integer.toString(value, 10))

    /**
     * Returns a JsonValue instance that represents the given <code>long</code> value.
     *
     * @param value
     *          the value to get a JSON representation for
     * @return a JSON value that represents the given value
     */
    fun value(value: Long): JsonValue = JsonNumber(value.toString()) // (Long.toString(value, 10));

    /**
     * Returns a JsonValue instance that represents the given <code>float</code> value.
     *
     * @param value
     *          the value to get a JSON representation for
     * @return a JSON value that represents the given value
     */
    fun value(value: Float): JsonValue {
        if (value.isInfinite() || value.isNaN()) {
            throw IllegalArgumentException("Infinite and NaN values not permitted in JSON")
        }
        return JsonNumber(cutOffPointZero(value.toString()))
    }

    /**
     * Returns a JsonValue instance that represents the given <code>double</code> value.
     *
     * @param value
     *          the value to get a JSON representation for
     * @return a JSON value that represents the given value
     */
    fun value(value: Double): JsonValue {
        if (value.isInfinite() || value.isNaN()) {
            throw IllegalArgumentException("Infinite and NaN values not permitted in JSON")
        }
        return JsonNumber(cutOffPointZero(value.toString()))
    }

    /**
     * Returns a JsonValue instance that represents the given string.
     *
     * @param string
     *          the string to get a JSON representation for
     * @return a JSON value that represents the given string
     */
    fun value(string: String?): JsonValue = if (string == null) NULL else JsonString(string)

    /**
     * Returns a JsonValue instance that represents the given <code>boolean</code> value.
     *
     * @param value
     *          the value to get a JSON representation for
     * @return a JSON value that represents the given value
     */
    fun value(value: Boolean) = if (value) TRUE else FALSE

    /**
     * Creates a new empty JsonArray. This is equivalent to creating a new JsonArray using the
     * constructor.
     *
     * @return a new empty JSON array
     */
    fun array() = JsonArray()

    /**
     * Creates a new JsonArray that contains the JSON representations of the given <code>int</code>
     * values.
     *
     * @param values
     *          the values to be included in the new JSON array
     * @return a new JSON array that contains the given values
     */
    fun array(vararg values: Int): JsonArray {
        val array = JsonArray()
        for (value in values) {
            array.add(value)
        }
        return array
    }

    /**
     * Creates a new JsonArray that contains the JSON representations of the given <code>long</code>
     * values.
     *
     * @param values
     *          the values to be included in the new JSON array
     * @return a new JSON array that contains the given values
     */
    fun array(vararg values: Long): JsonArray {
        val array = JsonArray()
        for (value in values) {
            array.add(value)
        }
        return array
    }

    /**
     * Creates a new JsonArray that contains the JSON representations of the given <code>float</code>
     * values.
     *
     * @param values
     *          the values to be included in the new JSON array
     * @return a new JSON array that contains the given values
     */
    fun array(vararg values: Float): JsonArray {
        val array = JsonArray()
        for (value in values) {
            array.add(value)
        }
        return array
    }

    /**
     * Creates a new JsonArray that contains the JSON representations of the given <code>double</code>
     * values.
     *
     * @param values
     *          the values to be included in the new JSON array
     * @return a new JSON array that contains the given values
     */
    fun array(vararg values: Double): JsonArray {
        val array = JsonArray()
        for (value in values) {
            array.add(value)
        }
        return array
    }

    /**
     * Creates a new JsonArray that contains the JSON representations of the given
     * <code>boolean</code> values.
     *
     * @param values The values to be included in the new JSON array
     * @return a new JSON array that contains the given values
     */
    fun array(vararg values: Boolean): JsonArray {
        val array = JsonArray()
        for (value in values) {
            array.add(value)
        }
        return array
    }

    /**
     * Creates a new JsonArray that contains the JSON representations of the given strings.
     *
     * @param values The strings to be included in the new JSON array
     *
     * @return a new JSON array that contains the given strings
     */
    fun array(vararg values: String): JsonArray {
        val array = JsonArray()
        for (value in values) {
            array.add(value)
        }
        return array
    }

    /**
     * Creates a new empty JsonObject. This is equivalent to creating a new JsonObject using the
     * constructor.
     *
     * @return a new empty JSON object
     */
    fun jsonObject() = JsonObject()

    /**
     * Parses the given input string as JSON. The input must contain a valid JSON value, optionally
     * padded with whitespace.
     *
     * @param string the input string, must be valid JSON
     * @return a value that represents the parsed JSON
     * @throws ParseException if the input is not valid JSON
     */
    fun parse(string: String): JsonValue {
        val handler = DefaultHandler()
        parse(string, handler)
        return handler.value
    }

    /**
     * Parses the given input string. The input must contain a valid JSON value, optionally padded
     * with whitespace.
     *
     * @param string the input string, must be valid JSON
     * @throws ParseException if the input is not valid JSON
     */
    fun parse(string: String, handler: JsonHandler<*, *>) {
        val bufferSize = max(JsonParser.MIN_BUFFER_SIZE, min(JsonParser.DEFAULT_BUFFER_SIZE, string.length))
        JsonParser(handler, MPPStringReader(string), bufferSize).parse()
    }

    /**
     * Parses the given stream as JSON. The input must contain a valid JSON value, optionally
     * padded with whitespace.
     *
     * @param reader the input, must contain valid JSON
     * @return a value that represents the parsed JSON
     * @throws ParseException if the input is not valid JSON
     */
    fun parse(reader: MPPReader, bufferSize: Int = JsonParser.DEFAULT_BUFFER_SIZE): JsonValue {
        val handler = DefaultHandler()
        parse(reader, handler, bufferSize)
        return handler.value

    }

    /**
     * Reads the entire input from the given reader and parses it as JSON. The input must contain a
     * valid JSON value, optionally padded with whitespace.
     *
     * @param reader the reader to read the input from
     * @throws ParseException if the input is not valid JSON
     */
    fun parse(reader: MPPReader, handler: JsonHandler<*, *>, bufferSize: Int = JsonParser.DEFAULT_BUFFER_SIZE) {
        JsonParser(handler, reader, bufferSize).parse()
    }

    private fun cutOffPointZero(string: String): String {
        if (string.endsWith(".0")) {
            return string.substring(0, string.length - 2)
        }
        return string
    }

    class DefaultHandler : JsonHandler<JsonObject, JsonArray>() {

        lateinit var value: JsonValue
            protected set

        override fun startArray() = JsonArray()

        override fun startObject() = JsonObject()

        override fun endNull() {
            value = NULL
        }

        override fun endBoolean(value: Boolean) {
            this.value = if (value) TRUE else FALSE
        }

        override fun endString(string: String) {
            value = JsonString(string)
        }

        override fun endNumber(string: String) {
            value = JsonNumber(string)
        }

        override fun endArray(array: JsonArray) {
            value = array
        }

        override fun endObject(obj: JsonObject) {
            value = obj
        }

        override fun endArrayValue(array: JsonArray) {
            array.add(value)
        }

        override fun endObjectValue(obj: JsonObject, name: String) {
            obj.add(name, value)
        }

    }

}
